import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronDown, Sparkles } from "lucide-react";
import Splash from "@/components/splash/Splash";
import { getImageUrl } from "@/config/images";
import SmileyLoader from "./SmileyLoader";

/*
 * The home hero as it was, opened by a loader:
 *   run    a spray-painted smiley (SmileyLoader.jsx: full on desktop, lite on phones) is sprayed on and trembles while the
 *          van photo loads
 *   burst  once it is done AND the photo is ready: it swells and pops into a splash, flash,
 *          the logo splash splats out and the logo lands, then the text and buttons
 *   done   final frame (reduced motion, coming back to Home, or once the burst has played)
 * Burst timings are in App.css ("INTRO"), relative to the moment .intro-burst is set.
 */
let introPlayed = false;

// the van photo, sized for the screen (the original JPEG is 3.8MB / 4032px)
const HERO_BG =
  typeof window !== "undefined" && window.innerWidth < 900
    ? "food/web/food-truck-1080.webp"
    : "food/web/food-truck-1920.webp";
const RUN_MS = 2000; // from the moment the smiley starts spraying until it may pop
const MAX_WAIT_MS = 7000; // never hold the page longer than this
const SETTLE_MS = 2600; // burst animations are over by then

// The logo artwork placed in splash coordinates (the splash paths were traced from this file)
const LOGO_BOX = { x: -216, y: -157, width: 1878, height: 1305 };
const LOGO_VIEWBOX = `${LOGO_BOX.x} ${LOGO_BOX.y} ${LOGO_BOX.width} ${LOGO_BOX.height}`;

const IntroHero = () => {
  const bgRef = useRef(null);
  const [smileyReady, setSmileyReady] = useState(false);
  const onSmileyReady = useCallback(() => setSmileyReady(true), []);
  const [phase, setPhase] = useState(() => {
    const reduced =
      typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const alreadyPlayed = introPlayed;
    introPlayed = true;
    return reduced || alreadyPlayed ? "done" : "run";
  });
  const loading = phase === "run";
  const burst = !loading;

  // Burst when the smiley has played AND the van photo is decoded (or we have waited long enough)
  const photoReady = useRef(false);
  const smileyDone = useRef(false);
  useEffect(() => {
    if (phase !== "run") return undefined;
    const go = () => {
      if (photoReady.current && smileyDone.current) setPhase("burst");
    };
    const onPhoto = () => {
      photoReady.current = true;
      go();
    };
    const img = new Image();
    img.src = getImageUrl(HERO_BG);
    (img.decode ? img.decode() : Promise.reject()).then(onPhoto, () => {
      if (img.complete) onPhoto();
      else img.onload = img.onerror = onPhoto;
    });
    const giveUpTimer = setTimeout(() => {
      photoReady.current = true;
      smileyDone.current = true;
      go();
    }, MAX_WAIT_MS);
    return () => clearTimeout(giveUpTimer);
  }, [phase]);

  useEffect(() => {
    if (phase !== "run" || !smileyReady) return undefined;
    const timer = setTimeout(() => {
      smileyDone.current = true;
      if (photoReady.current) setPhase("burst");
    }, RUN_MS);
    return () => clearTimeout(timer);
  }, [phase, smileyReady]);

  // Once the burst has played, settle on the final frame (drops the loader, adds the logo shadow)
  useEffect(() => {
    if (phase !== "burst") return undefined;
    const timer = setTimeout(() => setPhase("done"), SETTLE_MS);
    return () => clearTimeout(timer);
  }, [phase]);

  // Parallax on the background, written straight to the element once per frame (no re-render)
  useEffect(() => {
    const el = bgRef.current;
    if (!el || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    let frame = 0;
    const update = () => {
      frame = 0;
      el.style.transform = `translate3d(0, ${(window.scrollY * 0.3).toFixed(1)}px, 0)`;
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  // No scrolling under the loader
  useEffect(() => {
    if (!loading) return undefined;
    const { overflow } = document.documentElement.style;
    document.documentElement.style.overflow = "hidden";
    return () => {
      document.documentElement.style.overflow = overflow;
    };
  }, [loading]);

  return (
    <section
      className={`relative min-h-screen flex items-center justify-center overflow-hidden ${burst ? "intro-burst" : ""} ${phase === "done" ? "intro-done" : ""}`}
    >
      {/* Animated background blobs */}
      <div className="absolute inset-0 particles-bg" />

      {/* Background with parallax */}
      <div
        ref={bgRef}
        className="absolute inset-0 bg-cover bg-center will-change-transform"
        style={{ backgroundImage: `url('${getImageUrl(HERO_BG)}')` }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/90 via-black/80 to-black" />

      {/* Grid pattern overlay */}
      <div className="absolute inset-0 bg-grid-pattern opacity-30" />

      {/* Graffiti overlay effect */}
      <div className="absolute inset-0 spray-texture" />

      {/* Floating decorative elements */}
      <div className="absolute top-20 left-10 w-20 h-20 border-2 border-yellow-400/20 rounded-full animate-float opacity-50" />
      <div className="absolute bottom-40 right-20 w-32 h-32 border-2 border-pink-500/20 rounded-full animate-float delay-300 opacity-50" />
      <div className="absolute top-1/3 right-1/4 animate-bounce-subtle">
        <Sparkles className="text-yellow-400/30" size={32} />
      </div>

      {/* Hero Content */}
      <div className="intro-shake relative z-10 text-center px-4 max-w-5xl mx-auto">
        <div className="mb-6">
          <Link to="/">
            <Splash
              variant="logo"
              animate
              play={burst}
              delay={250}
              viewBox={LOGO_VIEWBOX}
              className="intro-main h-44 md:h-64 w-auto mx-auto hover:scale-105 transition-transform duration-500 cursor-pointer text-[#ECCE21]"
              style={{ aspectRatio: `${LOGO_BOX.width} / ${LOGO_BOX.height}` }}
              role="img"
              aria-label="VROMIARA - THE GOURMET STREET FOOD"
              data-testid="hero-logo"
            >
              <image
                className="intro-logo"
                href={getImageUrl("logos/vromiara-logo-truly-transparent.png")}
                x={LOGO_BOX.x}
                y={LOGO_BOX.y}
                width={LOGO_BOX.width}
                height={LOGO_BOX.height}
              />
            </Splash>
          </Link>
        </div>

        <div className="intro-copy text-white mt-14 mb-8 tracking-wide font-adelia italic leading-tight" style={{ textShadow: 'none' }}>
          <p style={{ fontSize: 'clamp(1rem, 4.5vw, 2rem)' }}>Street Food Pioneers</p>
          <p style={{ fontSize: 'clamp(0.75rem, 3vw, 1.25rem)' }} className="text-gray-300 mt-1">Est 2015</p>
        </div>

        <div className="intro-copy intro-copy-late flex flex-col sm:flex-row gap-4 justify-center items-center">
          <Link
            to="/menu"
            className="magnetic-btn bg-yellow-400 text-black px-8 py-4 font-bold text-lg rounded transition-all duration-300 hover:bg-pink-500 hover:text-white pulse-neon group"
            data-testid="hero-menu-btn"
          >
            <span className="flex items-center gap-2">
              CHECK THE MENU
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </span>
          </Link>
          <Link
            to="/locations"
            className="magnetic-btn border-2 border-yellow-400 text-yellow-400 px-8 py-4 font-bold text-lg rounded transition-all duration-300 hover:bg-yellow-400 hover:text-black glitch-hover backdrop-blur-sm"
            data-testid="hero-locations-btn"
          >
            FIND A SPOT
          </Link>
        </div>

        {/* Scroll indicator */}
        <div className="intro-cue mt-12">
          <div className="flex flex-col items-center gap-2 animate-bounce">
            <span className="text-yellow-400/60 text-xs tracking-widest uppercase">Scroll</span>
            <ChevronDown size={28} className="text-yellow-400" />
          </div>
        </div>
      </div>

      {/* Bottom gradient fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black to-transparent" />

      {/* Loader: on top of everything until the burst */}
      {phase !== "done" && (
        <div className="intro-loader" onClick={() => setPhase((p) => (p === "run" ? "burst" : p))}>
          <SmileyLoader onReady={onSmileyReady} />
        </div>
      )}
      <div className="intro-flash" aria-hidden="true" />
    </section>
  );
};

export default IntroHero;
